import { ChangeDetectionStrategy, Component, effect, input, model, output, signal } from '@angular/core';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { EllipsisTooltipComponent } from '@shared/components/ellipsis-tooltip/ellipsis-tooltip.component';
import { PrimeTemplate } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { UserAccount } from '@shared/interfaces';
import { HttpStatusEnum } from '@core/api';
import { AutosuggestApi } from '@shared/api';
import { AutocompleteAccountsTypeEnum } from './autocomplete-accounts.helper';

@Component({
    selector: 'autocomplete-accounts[select]',
    standalone: true,
    imports: [AutoCompleteModule, EllipsisTooltipComponent, PrimeTemplate, TranslateModule, FormsModule],
    templateUrl: './autocomplete-accounts.component.html',
    styleUrl: './autocomplete-accounts.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteAccountsComponent {
    id = input.required<string>();
    showFullNameInSelection = input<boolean>(true);
    disabled = input<boolean>(false);
    type = input<AutocompleteAccountsTypeEnum>(AutocompleteAccountsTypeEnum.ACCOUNTS);

    searchValue = model<string>('');
    selectedAccount = signal<UserAccount | null>(null);
    suggestions = signal<UserAccount[]>([]);

    select = output<UserAccount | null>();
    valueChanged = output<string>();

    constructor(private autosuggestApi: AutosuggestApi) {
        effect(
            () => {
                if (!this.searchValue()) {
                    this.selectedAccount.set(null);
                }
            },
            { allowSignalWrites: true }
        );
    }

    async getUserSuggestions(): Promise<void> {
        if (this.type() === AutocompleteAccountsTypeEnum.ACCOUNTS) {
            const response = await this.autosuggestApi.suggestAccountsForImpersonation({
                searchValue: this.selectedAccount()?.email || this.searchValue(),
                take: 30,
            });

            if (response.status !== HttpStatusEnum.SUCCESS) {
                return;
            }

            this.suggestions.set(response.data || []);
            return;
        }

        if (this.type() === AutocompleteAccountsTypeEnum.CENTRAL_SHIPPING) {
            const response = await this.autosuggestApi.suggestCentralShippingUsers({
                searchValue: this.selectedAccount()?.email || this.searchValue(),
                take: 30,
            });
            this.suggestions.set(response.data?.users ?? []);
            return;
        }
    }

    selectAccount(user: UserAccount): void {
        this.selectedAccount.set({
            ...user,
            label: `${user.firstName || ' '} ${user.lastName || ''} ${user.email ? '(' + user.email + ')' : ''}`,
        });
        this.select.emit(user);
        this.valueChanged.emit(user.email);
        this.suggestions.set([]);
    }

    clearSelection(): void {
        this.selectedAccount.set(null);
        this.select.emit(null);
        this.getUserSuggestions();
    }
}
